/* eslint-disable react-hooks/exhaustive-deps */ function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect, useRef, useCallback } from "react";
import lodash from "lodash";
import DocumentManager from "./DocumentManager.js";
import Page from "./ContextPage";
import Section from "./DocumentSection";
// styles
import "../styles.css";
const DEFAULT_CONFIG = {
    layout: "a4",
    differentFirstPage: false,
    disclaimer: "",
    margin: 25
};
/**

 * DocumentComponent is a React functional component that renders sections into a document that resembles a word processor.
 * 
 * @param {Object} props - Object containing properties of DocumentComponent
 * @param {string} [props.id="document-report"] - An identifier for the component.
 * @param {Array} props.sections - An array of <div /> sections to be rendered in the document.
 * @param {Object} [props.metadata={}] - A dictionary of metadata for the document. Defaults to an empty dictionary.
 * @param {string} [props.metadata.company_logo=""] - url blob for header logo
 * @param {string} [props.metadata.disclaimer=""] - a disclaimer placed in footer
 * 
 * @param {boolean} [props.visible=true] - A flag indicating if the document should be visible.
 * @param {function} [props.onScale=null] - A callback function to be executed when the document is scaled.
 * 
 * @param {Object} [props.config={}] - A dictionary of configuration options for the document. Defaults to an empty dictionary.
 * @param {boolean} [props.config.differentFirstPage=false] - A flag indicating if the first page should be rendered differently.
 * @param {number} [props.config.margin=25] - The pixel height added as buffer to each page when determining number of sections will fit on each page before a new page is created.
 * @param {string} [props.config.layout="a4"] - The layout of the document.
 * 
 * The component uses the useState hook to manage the state of the document, the useRef hook to refer to the document's canvas and scale, and the lodash.debounce function to delay the execution of the updatePageChildren function.
 * The updatePageChildren function loads the sections into the document, gets the children of the document, and updates the state of the pageChildren if there are any new children.
 * 
 * @returns {JSX.Element} The DocumentComponent as a JSX Element
 */ export default function DocumentComponent(_param) {
    var { id = "document-report", sections, metadata = {}, visible = true, onScale = null, config = _object_spread({}, DEFAULT_CONFIG) } = _param, props = _object_without_properties(_param, [
        "id",
        "sections",
        "metadata",
        "visible",
        "onScale",
        "config"
    ]);
    const [Document] = useState(new DocumentManager(config));
    const [pageChildren, setPageChildren] = useState([]);
    const canvasRef = useRef(null);
    const scaleRef = useRef(null);
    const [scaledStyling, setScaledStyling] = useState({});
    const updatePageChildren = lodash.debounce(()=>{
        Document.loadSections();
        const nextPageChildren = Document.getChildren();
        if (nextPageChildren.length) {
            setPageChildren(nextPageChildren);
        }
    }, 500);
    const updateSectionHeight = (sectionIndex = 0, height = 0)=>{
        Document.updateSection(sectionIndex, {
            height
        });
        updatePageChildren();
    };
    const handleScale = (scale = 1)=>{
        if (scaleRef.current) {
            const newHeight = scaleRef.current.scrollHeight * scale;
            const scaledStyling = {
                height: `${newHeight}px`,
                transform: `scale(${scale})`
            };
            onScale === null || onScale === void 0 ? void 0 : onScale(scale);
            setScaledStyling(scaledStyling);
        }
    };
    const checkPage = useCallback((pageIndex = 0)=>{
        // returns bool based on rules
        const { differentFirstPage = false } = config;
        if (differentFirstPage) {
            return pageIndex === 0;
        }
        return false;
    }, [
        config
    ]);
    useEffect(()=>{
        // if children update, reset all children
        Document.reset();
        const nextSections = Document.syncSections(sections);
        Document.loadSections(nextSections);
        updatePageChildren();
    }, [
        sections
    ]);
    useEffect(()=>{
        Document.attachResizeListener(canvasRef, handleScale);
        return ()=>{
            Document.cleanup();
        };
    }, []);
    return /*#__PURE__*/ _jsx("div", {
        id: id,
        style: _object_spread_props(_object_spread({}, styles.container), {
            visibility: visible ? "visible" : "hidden"
        }),
        children: /*#__PURE__*/ _jsx("div", _object_spread_props(_object_spread({
            ref: canvasRef,
            style: styles.document
        }, props), {
            children: /*#__PURE__*/ _jsx("div", {
                id: "scaled-wrapper",
                ref: scaleRef,
                className: "print-scale-reset printableDiv",
                style: _object_spread({}, styles.pages, scaledStyling),
                children: pageChildren.map((pageSections = [], index)=>/*#__PURE__*/ _jsx("div", {
                        id: `page-${index}`,
                        className: "canvas-item page",
                        children: /*#__PURE__*/ _jsx(Page, {
                            number: index + 1,
                            header: checkPage(index) === false,
                            showDisclaimer: checkPage(index) === true,
                            metadata: metadata,
                            children: pageSections.map((section = {})=>/*#__PURE__*/ _jsx(Section, {
                                    id: section.id,
                                    sectionIndex: section.order,
                                    onUpdate: updateSectionHeight,
                                    children: section.child
                                }, section.id))
                        })
                    }, `page-${index}`))
            })
        }))
    });
}
const styles = {
    container: {
        fontFamily: "Roboto, Helvetica, Arial, sans-serif",
        width: "100%",
        padding: "20px 0",
        flex: 1,
        backgroundColor: "#e6e6e6",
        overflow: "auto"
    },
    document: {
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        backgroundColor: "#e6e6e6"
    },
    pages: {
        display: "flex",
        flexDirection: "column",
        width: "auto",
        gap: "20px",
        transformOrigin: "top center"
    }
};
